import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface GalleryItem {
  id: number;
  image_url: string;
  thumbnail_url?: string;
  title?: string;
  description?: string;
  alt_text?: string;
  order: number;
}

interface ImageGallery {
  id: number;
  title: string;
  description?: string;
  cover_image?: string;
  category?: string;
  items?: GalleryItem[];
}

const ImageGalleryAlbum: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [gallery, setGallery] = useState<ImageGallery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    if (id) {
      fetchGallery();
    }
  }, [id]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/cms/image-galleries');
      const foundGallery = response.data.find((g: ImageGallery) => g.id === parseInt(id || '0'));
      if (foundGallery) {
        setGallery(foundGallery);
      } else {
        setError('Gallery not found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load gallery');
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (item: GalleryItem, index: number) => {
    setSelectedImage(item);
    setCurrentIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (!gallery?.items) return;
    const nextIndex = (currentIndex + 1) % gallery.items.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(gallery.items[nextIndex]);
  };

  const prevImage = () => {
    if (!gallery?.items) return;
    const prevIndex = (currentIndex - 1 + gallery.items.length) % gallery.items.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(gallery.items[prevIndex]);
  };

  useEffect(() => {
    if (!showLightbox || !gallery?.items) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (gallery.items) {
          const nextIndex = (currentIndex + 1) % gallery.items.length;
          setCurrentIndex(nextIndex);
          setSelectedImage(gallery.items[nextIndex]);
        }
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (gallery.items) {
          const prevIndex = (currentIndex - 1 + gallery.items.length) % gallery.items.length;
          setCurrentIndex(prevIndex);
          setSelectedImage(gallery.items[prevIndex]);
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showLightbox, currentIndex, gallery]);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <h3>{t('public.galleries.loading')}</h3>
      </div>
    );
  }

  if (error || !gallery) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '40px'
      }}>
        <h3>{error || t('public.galleries.notFound')}</h3>
        <button className="btn btn-primary" onClick={() => navigate('/galleries')}>
          {t('public.galleries.backToGalleries')}
        </button>
      </div>
    );
  }

  return (
    <div className="image-gallery-album" style={{ 
      minHeight: '80vh',
      background: '#f8f9fa',
      padding: '40px 20px'
    }}>
      {/* Header */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto 40px',
        background: '#fff',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/galleries')}
          style={{ marginBottom: '20px' }}
        >
          <i className="fas fa-arrow-left"></i> {t('public.galleries.backToGalleries')}
        </button>
        <h1 style={{ 
          fontSize: '2.5rem', 
          marginBottom: '15px',
          color: '#333'
        }}>
          {gallery.title}
        </h1>
        {gallery.description && (
          <p style={{ 
            fontSize: '1.1rem', 
            color: '#666',
            lineHeight: '1.6'
          }}>
            {gallery.description}
          </p>
        )}
        {gallery.category && (
          <span style={{ 
            display: 'inline-block',
            background: '#e9ecef',
            padding: '6px 15px',
            borderRadius: '20px',
            fontSize: '14px',
            color: '#666',
            fontWeight: '500',
            marginTop: '15px'
          }}>
            {gallery.category}
          </span>
        )}
      </div>

      {/* Images Grid */}
      {gallery.items && gallery.items.length > 0 ? (
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '20px'
          }}>
            {gallery.items.map((item, index) => (
              <div
                key={item.id}
                onClick={() => openLightbox(item, index)}
                style={{
                  position: 'relative',
                  paddingBottom: '100%',
                  height: 0,
                  overflow: 'hidden',
                  background: '#f0f0f0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
              >
                <img 
                  src={item.thumbnail_url || item.image_url} 
                  alt={item.alt_text || item.title || `Image ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = item.image_url;
                  }}
                />
                {item.title && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    color: '#fff',
                    padding: '15px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {item.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 20px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <i className="fas fa-images" style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3, color: '#999' }}></i>
          <h3 style={{ color: '#333', marginBottom: '10px' }}>{t('public.galleries.noImages')}</h3>
          <p style={{ color: '#666' }}>{t('public.galleries.noImagesDescription')}</p>
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && selectedImage && gallery.items && (
        <div 
          className="lightbox-overlay"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: '#fff',
              fontSize: '28px',
              cursor: 'pointer',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            &times;
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            style={{
              position: 'absolute',
              left: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            style={{
              position: 'absolute',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <img
              src={selectedImage.image_url}
              alt={selectedImage.alt_text || selectedImage.title || 'Gallery image'}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            {(selectedImage.title || selectedImage.description) && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '20px',
                borderRadius: '8px',
                marginTop: '20px',
                maxWidth: '600px',
                color: '#333'
              }}>
                {selectedImage.title && (
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                    {selectedImage.title}
                  </h3>
                )}
                {selectedImage.description && (
                  <p style={{ margin: 0, fontSize: '16px', color: '#666', lineHeight: '1.6' }}>
                    {selectedImage.description}
                  </p>
                )}
              </div>
            )}
            <div style={{
              color: '#fff',
              marginTop: '15px',
              fontSize: '14px'
            }}>
              {currentIndex + 1} / {gallery.items.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGalleryAlbum;

